import { Core } from "@faire/design-tokens";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { isManuallyCroppedImage } from "@faire/web/common/images/optimizeImages";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { useRenderTracing } from "@faire/web/common/tracing/useRenderTracing";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { observer } from "mobx-react";
import React from "react";
import styled from "styled-components";

import { ProgressiveBrandCoverImage } from "@faire/retailer/components/Image/ProgressiveBrandCoverImage";
import { PageLoadEvent } from "@faire/retailer/services/performance/const";
import { PerformanceMarker } from "@faire/retailer/services/performance/PerformanceMarker";
import {
  BANNER_HEIGHT_DESKTOP,
  BANNER_HEIGHT_TABLET,
} from "@faire/retailer/views/brand/BrandHeader/bannerHeightConsts";
import { buildBannerImageSrcSet } from "@faire/retailer/views/brand/BrandHeader/buildBannerImageSrcSet";

const PictureContainer = styled.picture<{
  $isManuallyCropped: boolean;
  $removeBackgroundColor?: boolean;
  $bannerHeight: number;
}>`
  background-color: ${(props) =>
    !props.$removeBackgroundColor ? Core.surface.muted : ""};
  width: 100%;

  height: ${BANNER_HEIGHT_DESKTOP}px;

  filter: brightness(98%);

  ${responsiveMediaQueries.tablet} {
    height: ${BANNER_HEIGHT_TABLET}px;
  }

  ${responsiveMediaQueries.mobile} {
    height: ${(props) => props.$bannerHeight}px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: ${({ $isManuallyCropped: isManuallyCropped }) =>
      isManuallyCropped ? "center" : "right center"};
  }
`;

const handleBannerPictureLoaded = () =>
  PerformanceMarker.get().mark(PageLoadEvent.firstMeaningfulPaint);
interface IProps {
  brandImage: IImage | undefined;
  height?: number;
  removeBackgroundColor?: boolean;
}

const BannerPictureBase: React.FC<IProps> = (props) => {
  useTraceComponentRender("BannerPictureBase");
  const endTrace = useRenderTracing("BrandBannerImage");
  const { brandImage, removeBackgroundColor } = props;

  const isSSR = useIsSSR();
  const { isMobile } = useViewport();

  if (!brandImage || !brandImage.url) {
    endTrace();
    return null;
  }

  const bannerHeight = isMobile ? 120 : 240;

  const { srcset, sizes } = buildBannerImageSrcSet(brandImage);

  return (
    <PictureContainer
      $isManuallyCropped={isManuallyCroppedImage(brandImage)}
      onLoad={handleBannerPictureLoaded}
      $removeBackgroundColor={removeBackgroundColor}
      $bannerHeight={props.height ?? bannerHeight}
    >
      {isSSR ? <source srcSet={srcset} sizes={sizes} /> : null}
      <ProgressiveBrandCoverImage
        image={brandImage}
        isSSR={false}
        alt={brandImage.alt_text ?? "Brand banner picture"}
        isManuallyCropped={isManuallyCroppedImage(brandImage)}
        onLoad={endTrace}
      />
    </PictureContainer>
  );
};

export const BannerPicture = observer(BannerPictureBase);
